// src/contexts/useAuth.js
// ======================================================
// 🔐 USE AUTH HOOK - NIVEL ENTERPRISE (AIRBNB/ZILLOW)
// ======================================================
// ✅ Acceso seguro al contexto de autenticación
// ✅ Funciones auxiliares para permisos y UI
// ✅ Compatible con modo DEMO y PRODUCCIÓN
// ✅ Zero breaking changes
// ======================================================

import { useContext } from 'react';
import { AuthContext } from './AuthProvider';

export function useAuth() {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  
  // ======================================================
  // 🔐 VERIFICACIÓN DE PERMISOS (NIVEL ENTERPRISE)
  // ======================================================
  
  /**
   * Verifica si el usuario tiene un permiso específico
   * @param {string} permission - Nombre del permiso a verificar
   * @returns {boolean}
   */
  const hasPermission = (permission) => {
    if (!context.user) return false;
    
    // Si el usuario tiene el objeto _permissions (nuevo formato)
    if (context.user._permissions) {
      // Permisos booleanos directos
      if (permission === 'create') return context.user._permissions.create === true;
      if (permission === 'read') return context.user._permissions.read === true;
      if (permission === 'update') return context.user._permissions.update === true;
      if (permission === 'delete') return context.user._permissions.delete === true;
      if (permission === 'manageUsers') return context.user._permissions.manageUsers === true;
      if (permission === 'manageCompany') return context.user._permissions.manageCompany === true;
      if (permission === 'exportData') return context.user._permissions.exportData === true;
      if (permission === 'viewReports') return context.user._permissions.viewReports === true;
      if (permission === 'manageProperties') return context.user._permissions.manageProperties === true;
      if (permission === 'manageClients') return context.user._permissions.manageClients === true;
    }
    
    // Fallback: sistema basado en roles (compatibilidad)
    const role = context.user.role || context.user._securityLevel;
    
    const rolePermissions = {
      admin: ['all', 'create', 'read', 'update', 'delete', 'manageUsers', 'manageCompany', 'exportData', 'viewReports', 'manageProperties', 'manageClients'],
      corretor: ['create', 'read', 'update', 'viewReports', 'manageProperties', 'manageClients'],
      usuario: ['read', 'viewReports']
    };
    
    const userRole = role || 'usuario';
    const allowedPermissions = rolePermissions[userRole] || ['read'];
    
    return allowedPermissions.includes('all') || allowedPermissions.includes(permission);
  };
  
  /**
   * Verifica si el usuario puede escribir (crear/editar)
   * @returns {boolean}
   */
  const canWrite = () => {
    if (!context.user) return false;
    return context.user.canWrite !== false && hasPermission('create');
  };
  
  /**
   * Verifica si el usuario puede eliminar
   * @returns {boolean}
   */
  const canDelete = () => {
    if (!context.user) return false;
    return context.user.canDelete === true || hasPermission('delete');
  };
  
  /**
   * Verifica si el usuario es administrador
   * @returns {boolean}
   */
  const isAdmin = () => {
    if (!context.user) return false;
    return context.user.role === 'admin' || context.user._securityLevel === 'admin';
  };
  
  /**
   * Verifica si el usuario es corretor
   * @returns {boolean}
   */
  const isCorretor = () => {
    if (!context.user) return false;
    return context.user.role === 'corretor' || context.user._securityLevel === 'corretor';
  };
  
  // ======================================================
  // 👤 UTILIDADES DE USUARIO
  // ======================================================
  
  /**
   * Obtiene las iniciales del usuario para avatares
   * @returns {string}
   */
  const getUserInitials = () => {
    if (!context.user?.name) return 'U';
    
    const nameParts = context.user.name.trim().split(' ').filter(Boolean);
    
    if (nameParts.length === 0) return 'U';
    if (nameParts.length === 1) return nameParts[0].substring(0, 2).toUpperCase();
    
    return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
  };
  
  /**
   * Obtiene el nombre completo del rol (traducido)
   * @returns {string}
   */
  const getUserRoleName = () => {
    const role = context.user?.role || context.user?._securityLevel;
    
    const roleNames = {
      admin: 'Administrador',
      corretor: 'Corretor',
      usuario: 'Usuário',
      user: 'Usuário'
    };
    
    return roleNames[role] || 'Usuário';
  };
  
  /**
   * Obtiene el nombre para mostrar (nombre o email)
   * @returns {string}
   */
  const getDisplayName = () => {
    if (!context.user) return 'Usuário';
    
    if (context.user.name && context.user.name.trim()) {
      return context.user.name;
    }
    
    if (context.user.email) {
      return context.user.email.split('@')[0];
    }
    
    return 'Usuário';
  };
  
  /**
   * Obtiene el color del usuario (para avatares)
   * @returns {string}
   */
  const getUserColor = () => {
    return context.user?.color || '#2563eb';
  };
  
  /**
   * Obtiene el provider de autenticación (demo/firebase)
   * @returns {string}
   */
  const getAuthProvider = () => {
    return context.user?._authProvider || context.authMode || 'demo';
  };
  
  /**
   * Verifica si el email está verificado
   * @returns {boolean}
   */
  const isEmailVerified = () => {
    return context.user?.emailVerified === true;
  };
  
  // ======================================================
  // 🏢 UTILIDADES DE EMPRESA (WHITE LABEL)
  // ======================================================
  
  /**
   * Obtiene el ID de la empresa actual
   * @returns {string|null}
   */
  const getCompanyId = () => {
    return context.user?.companyId || context.user?.company || context.company?.companyId || null;
  };
  
  /**
   * Obtiene el nombre de la empresa
   * @returns {string}
   */
  const getCompanyName = () => {
    return context.company?.nome || context.user?.company || 'Imobiliária';
  };
  
  /**
   * Obtiene el color primario de la empresa
   * @returns {string}
   */
  const getCompanyColor = () => {
    return context.company?.cor || context.user?.color || '#2563eb';
  };
  
  // ======================================================
  // 📊 ESTADO DE AUTENTICACIÓN
  // ======================================================
  
  /**
   * Verifica si el usuario está autenticado
   * @returns {boolean}
   */
  const isAuthenticated = () => {
    return !!context.user?._isAuthenticated;
  };
  
  /**
   * Verifica si está en modo DEMO
   * @returns {boolean}
   */
  const isDemoMode = () => {
    return context.authMode === 'demo' || context.user?._authProvider === 'demo';
  };
  
  /**
   * Verifica si está en modo PRODUCCIÓN (Firebase)
   * @returns {boolean}
   */
  const isProductionMode = () => {
    return context.authMode === 'production' || context.user?._authProvider === 'firebase';
  };
  
  /**
   * Obtiene el estado completo de autenticación
   * @returns {Object}
   */
  const getAuthState = () => {
    return {
      isAuthenticated: isAuthenticated(),
      isLoading: context.loadingAuth,
      mode: context.authMode,
      isDemo: isDemoMode(),
      isProduction: isProductionMode(),
      user: context.user ? {
        id: context.user.id,
        name: getDisplayName(),
        email: context.user.email,
        role: getUserRoleName(),
        initials: getUserInitials(),
        emailVerified: isEmailVerified(),
        provider: getAuthProvider()
      } : null,
      company: context.company ? {
        id: getCompanyId(),
        name: getCompanyName(),
        color: getCompanyColor()
      } : null,
      permissions: {
        canWrite: canWrite(),
        canDelete: canDelete(),
        isAdmin: isAdmin(),
        isCorretor: isCorretor()
      }
    };
  };
  
  // ======================================================
  // 📤 RETORNO DEL HOOK
  // ======================================================
  
  return {
    // Contexto original (para compatibilidad)
    ...context,
    
    // Permisos (nuevo)
    hasPermission,
    canWrite,
    canDelete,
    isAdmin,
    isCorretor,
    
    // Utilidades de usuario
    getUserInitials,
    getUserRoleName,
    getDisplayName,
    getUserColor,
    getAuthProvider,
    isEmailVerified,
    
    // Utilidades de empresa
    getCompanyId,
    getCompanyName,
    getCompanyColor,
    
    // Estado de autenticación
    isAuthenticated,
    isDemoMode,
    isProductionMode,
    getAuthState
  };
}